import { toDate } from '@art-suite/art-core-ts';

import type { ApiActivityLog, ApiActivityLogEventType, ApiScan } from '@kaiizen/kai-pro-api';

/** The activity-log events that record a scan's approval, and so who approved it. */
export const SCAN_APPROVAL_EVENT_TYPES: readonly ApiActivityLogEventType[] = ['scopeApproved', 'changeOrderApproved'];

/** One scope version's section of an activity log: from its scan's creation until the next version's. */
export type ScanActivitySection = {
  id: string;
  scanId: string;
  /** 1 for the original scope, then 2, 3, … for each later version. */
  scopeVersion: number;
  /** The change order a later version is: version 2 is change order 1. Only for change-order scans. */
  changeOrderNumber?: number;
  /** When the section starts: the scan's creation. */
  startsAt: Date;
  /** The heading's timestamp: when the scan was approved, else when it was created. */
  at: Date;
  /** Who approved the scan; no one for a scan not yet approved. */
  actorUserId?: string;
  /** Approved, or still a draft (the working copy). */
  isApproved: boolean;
};

/** Who approved each scan, from newest-first approval events: a scan approved more than once takes its latest approver. */
export const getApproverUserIdByScanId = (approvalEvents: readonly ApiActivityLog[]): Map<string, string> => {
  const approverUserIdByScanId = new Map<string, string>();
  for (const { scanId, actorUserId } of approvalEvents) {
    if (scanId && actorUserId && !approverUserIdByScanId.has(scanId)) approverUserIdByScanId.set(scanId, actorUserId);
  }
  return approverUserIdByScanId;
};

/** A project's scope-version scans as activity-log sections, numbered in creation order. */
export const getScanActivitySections = ({ scopeVersionScans, approverUserIdByScanId }: {
  scopeVersionScans: readonly ApiScan[];
  approverUserIdByScanId: ReadonlyMap<string, string>;
}): ScanActivitySection[] =>
  [...scopeVersionScans]
    .sort((a, b) => toDate(a.createdAt).getTime() - toDate(b.createdAt).getTime())
    .map((scan, index) => ({
      id: `scan-${scan.id}`,
      scanId: scan.id,
      scopeVersion: index + 1,
      changeOrderNumber: scan.scanType === 'changeRequest' && index > 0 ? index : undefined,
      startsAt: toDate(scan.createdAt),
      at: toDate(scan.approvedAt ?? scan.createdAt),
      actorUserId: scan.approvedAt ? approverUserIdByScanId.get(scan.id) : undefined,
      isApproved: scan.scanLifecycleStatus === 'approved',
    }));
